import ConnectedContainer from "components/container/ConnectedContainer.tsx";
import { useAppDispatch, useAppSelector } from "config/store";
import { useMount } from "react-use";
import { authActions } from "config/store/modules/auth.ts";
import Button from "components/atom/button/Button.tsx";
import locationPng from "assets/images/location.png";
import { Fragment, useCallback, useMemo, useState } from "react";
import { EventDateType, EventType } from "globals/types/models.ts";
import dayjs from "dayjs";
import EventCard from "components/layouts/card/EventCard.tsx";
import { useNavigate } from "react-router-dom";
import ActionBlockedModal from "components/modals/ActionBlockedModal.tsx";
import { USER_PLAN_TYPES } from "globals/constants.ts";
import { getClosestNextDate } from "utils/data/event.ts";
import { useTranslation } from "react-i18next";

const now = dayjs();

function EventManagerProfilePage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const currentUser = useAppSelector(state => state.auth.user);
  const myEvents: EventType[] = useAppSelector(state => state.auth.myEvents);


  const [isOpenCantCreateEventModal, setIsOpenCantCreateEventModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useMount(async () => {
    await dispatch(authActions.getMyEvents());
    setIsLoading(false);
  });

  const pastEvents = useMemo(() => {
    return myEvents.filter(e => {
      return e.dates.filter(d => {
        if (!d.startDate) return false; //will be in future and
        return now.isAfter(d.startDate, "day");
      }).length === e.dates.length;

    });
  }, [myEvents]);

  const futureAndPresentEvents = useMemo(() => {
    //can use .sort since filter return a new array
    return myEvents.filter(e => {
      //return all events not in the past zone
      return !pastEvents.some(p => p.id === e.id);
    })
      .sort((a, b) => {
        const closestDateA = getClosestNextDate<EventDateType>(a.dates);
        const closestDateB = getClosestNextDate<EventDateType>(b.dates);
        if (!closestDateA) return 0;
        if (!closestDateB) return -1;
        return dayjs(closestDateA.startDate).isAfter(dayjs(closestDateB.startDate), "minutes") ? 1 : -1;
      });
  }, [pastEvents, myEvents]);

  const onEventClick = useCallback((event: EventType) => {
    navigate(`/profile/events/${event.uuid}`);
  }, [navigate]);

  const onBtnCreateEvent = useCallback(() => {
    if (!currentUser) return;

    if (currentUser.planType === USER_PLAN_TYPES.CANCELED) {
      //if no plan, cant create event
      setIsOpenCantCreateEventModal(true);
      return;//stop execution
    }

    //need to be in paid mode
    if (currentUser.planType === USER_PLAN_TYPES.TRIAL) {
      setIsOpenCantCreateEventModal(true);
      return;
      //show a modal to upgrade plan
      /* todo new trial dont check
      if (totalEventCountCreated >= 4) {
          setIsOpenCantCreateEventModal(true)
          return;//stop execution
      } */
      /*
        if (currentUser.trialEndedAt && dayjs(currentUser.trialEndedAt).isBefore(now)) {
            setIsOpenCantCreateEventModal(true)
            return;//stop execution
        } */
    }
    navigate("/profile/events/create");
  }, [navigate, currentUser]);


  return (
    <ConnectedContainer title={t("user.myProfile")}>

      {!isLoading && (
        <div className="pb-24 sm:pb-0">

          {myEvents.length === 0 && (
            <div
              className="mx-auto flex w-full flex-1 flex-col items-center justify-center gap-6 md:max-w-[600px]">
              <img src={locationPng} className="mx-auto block h-[200px] w-auto" />
              <h2 className="text-center text-xl text-gray-900">
                {t("user.noEventsTitle")}
              </h2>
              <p className="text-center">
                {t("user.noEventsDescription")}<br />
                {t("user.noEventsDescription2")}
              </p>
              <div className="hidden sm:block">
                <Button className="w-full" onClick={onBtnCreateEvent}>
                  {t("event.create")}
                </Button>
              </div>
            </div>
          )}

          {myEvents.length > 0 && (
            <div className="w-full">
              {futureAndPresentEvents.length > 0 && (
                <div className="">
                  <div className="flex flex-row items-center justify-between">
                    <h3 className="text-base sm:text-xl font-medium text-gray-900">
                      {t("event.upcomingEvents")}
                    </h3>
                    <Button className="hidden sm:flex" small onClick={onBtnCreateEvent}>
                      {t("event.create")}
                    </Button>
                  </div>
                  <div
                    className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                    {futureAndPresentEvents.map(event => (
                      <EventCard key={event.id} event={event} onClick={onEventClick} />
                    ))}
                  </div>
                </div>
              )}
              {pastEvents.length > 0 && (
                <div className="pt-12">
                  <div className="flex flex-row items-center justify-between">
                    <h3 className="text-base sm:text-xl font-medium text-gray-900">
                      {t("event.passedEvents")}
                    </h3>
                    {futureAndPresentEvents.length === 0 && (
                      <Button className="hidden sm:flex" small onClick={onBtnCreateEvent}>
                        {t("create")}<span
                        className="hidden sm:inline">&nbsp;{t("event.anEvent")}</span>
                      </Button>
                    )}
                  </div>
                  <div
                    className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                    {pastEvents.map(event => (
                      <EventCard key={event.id} event={event} onClick={onEventClick} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="block sm:hidden fixed bottom-28 left-2 right-2">
            <Button className="w-full" small onClick={onBtnCreateEvent}>
              {t("event.create")}
            </Button>
          </div>
        </div>
      )}

      <ActionBlockedModal isOpen={isOpenCantCreateEventModal}
                          onClose={() => setIsOpenCantCreateEventModal(false)}
      >
        {currentUser?.planType === USER_PLAN_TYPES.CANCELED && (
          <Fragment>
            <p className="text-center text-gray-400">
              {t("plans.pleaseSelectBeforeEvent")}
            </p>
          </Fragment>
        )}
        {currentUser?.planType === USER_PLAN_TYPES.TRIAL && (
          <Fragment>
            <p className="text-center text-gray-400">
              {t("plans.needToSelectPlan")}
            </p>
          </Fragment>
        )}

        <Button className="mt-12 block w-full" onClick={() => navigate("/profile/me/plans")}>
          {t("plans.seeAllPlans")}
        </Button>
      </ActionBlockedModal>
    </ConnectedContainer>
  );
}

export default EventManagerProfilePage;
